import React, { useEffect, useMemo, useState } from 'react';

const LEGAL_ROUTES = Object.freeze({
  '/terms': {
    title: 'Mindmappi Beta Terms of Use',
    source: '/legal/beta-terms-of-use.md',
  },
  '/terms/beta-2026-08': {
    title: 'Mindmappi Beta Terms of Use',
    source: '/legal/beta-terms-of-use.md',
  },
  '/privacy': {
    title: 'Mindmappi Beta Privacy Notice',
    source: '/legal/beta-privacy-notice.md',
  },
});

export function legalDocumentForPath(pathname) {
  return LEGAL_ROUTES[pathname.replace(/\/$/, '') || '/'] || null;
}

function inlineMarkdown(value, keyPrefix) {
  const parts = String(value).split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g);
  return parts.filter(Boolean).map((part, index) => {
    const key = `${keyPrefix}-${index}`;
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={key}>{part.slice(2, -2)}</strong>;
    }
    const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) {
      const external = /^https?:/.test(link[2]);
      return (
        <a key={key} href={link[2]} {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}>
          {link[1]}
        </a>
      );
    }
    return part;
  });
}

function cells(line) {
  return line.trim().replace(/^\||\|$/g, '').split('|').map((value) => value.trim());
}

function renderMarkdown(markdown) {
  const lines = markdown.replace(/\r/g, '').split('\n');
  const blocks = [];
  let index = 0;
  while (index < lines.length) {
    const line = lines[index].trim();
    if (!line) {
      index += 1;
      continue;
    }
    const heading = line.match(/^(#{1,3})\s+(.+)$/);
    if (heading) {
      const level = heading[1].length;
      const Tag = `h${level}`;
      blocks.push(<Tag key={`h-${index}`}>{inlineMarkdown(heading[2], `h-${index}`)}</Tag>);
      index += 1;
      continue;
    }
    if (line.startsWith('|') && /^\|?\s*:?-+/.test(lines[index + 1]?.trim() || '')) {
      const headers = cells(line);
      index += 2;
      const rows = [];
      while (index < lines.length && lines[index].trim().startsWith('|')) {
        rows.push(cells(lines[index]));
        index += 1;
      }
      const tableIndex = index;
      blocks.push(
        <div className="legal-document__table-wrap" key={`table-${tableIndex}`}>
          <table>
            <thead><tr>{headers.map((header, column) => <th key={column}>{inlineMarkdown(header, `th-${tableIndex}-${column}`)}</th>)}</tr></thead>
            <tbody>{rows.map((row, rowIndex) => (
              <tr key={rowIndex}>{row.map((cell, column) => <td key={column}>{inlineMarkdown(cell, `td-${tableIndex}-${rowIndex}-${column}`)}</td>)}</tr>
            ))}</tbody>
          </table>
        </div>
      );
      continue;
    }
    if (/^-\s+/.test(line)) {
      const items = [];
      while (index < lines.length && /^-\s+/.test(lines[index].trim())) {
        const item = [lines[index].trim().replace(/^-\s+/, '')];
        index += 1;
        while (
          index < lines.length
          && lines[index].trim()
          && !/^-\s+/.test(lines[index].trim())
          && !/^(#{1,3})\s+/.test(lines[index].trim())
          && !lines[index].trim().startsWith('|')
        ) {
          item.push(lines[index].trim());
          index += 1;
        }
        items.push(item.join(' '));
      }
      const listIndex = index;
      blocks.push(<ul key={`ul-${listIndex}`}>{items.map((item, itemIndex) => <li key={itemIndex}>{inlineMarkdown(item, `li-${listIndex}-${itemIndex}`)}</li>)}</ul>);
      continue;
    }
    const paragraph = [line];
    index += 1;
    while (
      index < lines.length
      && lines[index].trim()
      && !/^(#{1,3})\s+/.test(lines[index].trim())
      && !/^-\s+/.test(lines[index].trim())
      && !lines[index].trim().startsWith('|')
    ) {
      paragraph.push(lines[index].trim());
      index += 1;
    }
    blocks.push(<p key={`p-${index}`}>{inlineMarkdown(paragraph.join(' '), `p-${index}`)}</p>);
  }
  return blocks;
}

export default function LegalDocumentPage({ document }) {
  const [markdown, setMarkdown] = useState('');
  const [error, setError] = useState(false);
  useEffect(() => {
    let cancelled = false;
    fetch(document.source, { cache: 'no-cache' })
      .then((response) => {
        if (!response.ok) throw new Error(`Legal document request failed: ${response.status}`);
        return response.text();
      })
      .then((text) => { if (!cancelled) setMarkdown(text); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [document.source]);
  const content = useMemo(() => renderMarkdown(markdown), [markdown]);

  return (
    <main className="legal-document">
      <nav aria-label="Legal documents" className="legal-document__nav">
        <a href="/">Mindmappi</a>
        <span aria-hidden="true">·</span>
        <a href="/terms">Terms</a>
        <a href="/privacy">Privacy</a>
      </nav>
      <article aria-label={document.title}>
        {!markdown && !error && <p role="status">Loading document…</p>}
        {error ? <p role="alert">This document could not be loaded. Please try again.</p> : content}
      </article>
    </main>
  );
}
